<template>
  <a-drawer
    :width="900"
    :visible="visible"
    :unmount-on-close="true"
    :mask-closable="false"
    hide-cancel
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <template #title>
      <a-space>
        {{ title || '回访记录' }}
        <a-tooltip :content="$t('followTable.add_follow_up_record')">
          <a-button type="primary" size="mini" @click="showAddRecordModal">
            <template #icon>
              <icon-plus />
            </template>
            {{ $t('followTable.add_follow_up_record') }}
          </a-button>
        </a-tooltip>
      </a-space>
    </template>

    <a-table
      :row-key="(record: any) => record.id"
      :columns="followUpColumns"
      :data="recordList"
      :pagination="pagination"
      :loading="loading"
      @page-change="onPageChange"
    >
      <template #index="{ rowIndex }">
        {{ rowIndex + 1 + (pagination.current - 1) * pagination.pageSize }}
      </template>
      <template #createtime="{ record }">
        {{ record.createtime || '-' }}
      </template>
      <template #content="{ record }">
        {{ record.content || '-' }}
      </template>
      <template #staff_name="{ record }">
        {{ record.staff_name || '-' }}
      </template>
      <template #operations="{ record }">
        <a-button type="text" size="mini" @click="handleDelete(record)">
          <template #icon>
            <IconDelete />
          </template>
        </a-button>
      </template>
    </a-table>

    <!-- 添加回访记录Modal（标题栏入口，仅填写回访内容） -->
    <FollowUpRecordModal
      v-model:visible="addRecordVisible"
      mode="add"
      :show-result-level="false"
      :record="{
        user_id: props.userId,
        result_level: '',
        content: '',
        reminder_id: 0,
      }"
      @submit="handleAddRecordSubmit"
      @cancel="addRecordVisible = false"
    />
  </a-drawer>
</template>

<script lang="ts" setup>
  import { ref, reactive, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { IconPlus, IconDelete } from '@arco-design/web-vue/es/icon';
  import {
    addFollowUp,
    deleteFollowUp,
    queryFollowUpRecordList,
    FollowUpRecordInfo,
  } from '@/api/followup';
  import FollowUpRecordModal from '@/components/follow-up-record-modal/index.vue';
  import type { FollowUpRecordData } from '@/components/follow-up-record-modal/types';
  import useLoading from '@/hooks/loading';
  import { Message, Modal } from '@arco-design/web-vue';
  import { useUserStore } from '@/store';

  interface Props {
    visible: boolean;
    userId: number;
    title?: string;
  }

  interface Emits {
    (e: 'update:visible', value: boolean): void;
    (e: 'success'): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '',
  });

  const emit = defineEmits<Emits>();

  const { t } = useI18n();
  const { loading, setLoading } = useLoading(false);
  const userStore = useUserStore();

  const addRecordVisible = ref(false);

  const followUpColumns = computed(() => {
    const baseColumns: any[] = [
      {
        title: t('followTable.columns.index'),
        dataIndex: 'index',
        slotName: 'index',
        width: 80,
      },
      {
        title: '回访时间',
        dataIndex: 'createtime',
        slotName: 'createtime',
        width: 150,
      },
      {
        title: '回访内容',
        dataIndex: 'content',
        slotName: 'content',
        width: 300,
      },
    ];

    // 只有部门为3的用户才显示回访人列
    if (userStore.userInfo?.department === 3) {
      baseColumns.push({
        title: '回访人',
        dataIndex: 'staff_name',
        slotName: 'staff_name',
        width: 120,
      });
    }

    // 添加操作列
    baseColumns.push({
      title: '操作',
      dataIndex: 'operations',
      slotName: 'operations',
      width: 100,
      fixed: 'right',
    });

    return baseColumns;
  });

  const recordList = ref<FollowUpRecordInfo[]>([]);
  const pagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 获取回访记录列表：按客户 user_id 查询，不传 reminder_id 即可拿到该客户全部回访记录
  const fetchRecords = async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await queryFollowUpRecordList({
        page,
        pageSize: pagination.pageSize,
        user_id: props.userId,
      } as any);
      recordList.value = data.list || [];
      pagination.current = page;
      pagination.total = data.total;
    } catch (err) {
      // 错误提示已由接口拦截器统一处理
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (page: number) => {
    fetchRecords(page);
  };

  const handleOk = () => {
    emit('update:visible', false);
  };

  const handleCancel = () => {
    emit('update:visible', false);
  };

  const showAddRecordModal = () => {
    addRecordVisible.value = true;
  };

  const handleAddRecordSubmit = async (data: FollowUpRecordData) => {
    try {
      // 标题栏入口不关联具体提醒，reminder_id 固定传 0
      await addFollowUp({
        ...data,
        user_id: props.userId,
        reminder_id: 0,
      } as any);
      Message.success('操作成功');
      addRecordVisible.value = false;
      await fetchRecords(pagination.current);
      emit('success');
    } catch (error) {
      console.error('[FollowUpRecordDrawer] 添加回访记录失败:', error);
    }
  };

  const handleDelete = async (record: FollowUpRecordInfo) => {
    try {
      // 显示确认对话框
      await new Promise((resolve, reject) => {
        Modal.confirm({
          title: '确认删除',
          content: '您确定要删除这条回访记录吗？删除后无法恢复。',
          okText: '确定',
          cancelText: '取消',
          onOk: resolve,
          onCancel: reject,
        });
      });

      // 用户确认后执行删除操作
      await deleteFollowUp((record as any).id);
      await fetchRecords(pagination.current);
      Message.success('操作成功');
      emit('success');
    } catch (error) {
      // 如果用户取消删除，不显示错误信息
      if (error !== undefined) {
        Message.error('删除失败');
      }
    }
  };

  // 监听visible和userId变化，当打开时获取数据
  watch(
    () => [props.visible, props.userId],
    ([newVisible, newUserId], oldValues) => {
      // 只有当drawer打开且有有效的userId时才获取数据
      if (newVisible && newUserId) {
        const [oldVisible, oldUserId] = oldValues || [false, 0];
        // 避免重复请求：只有当visible从false变为true，或者userId发生变化时才请求
        if (
          (!oldVisible && newVisible) ||
          (newVisible && newUserId !== oldUserId)
        ) {
          fetchRecords();
        }
      }
    },
    { immediate: true }
  );
</script>

<script lang="ts">
  export default {
    name: 'FollowUpRecordDrawer',
  };
</script>
